import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, EmptyState, ErrorState, Icon, Input, StatusBadge, TableSkeletonRows, toast, type StatusKind } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { startDownload } from "../../../lib/start-download";
import { useApi } from "../../../session/session-provider";
import { useFormatters, type InvoiceOut } from "../shared";
import { invoiceStatus, needsGatewayChoice } from "./billing-model";
import { useHandleOrder } from "./checkout";
import { GatedButton } from "./gated-button";
import type { CheckoutChoiceBody } from "./gateway-chooser";
import { PayInvoiceDialog } from "./pay-invoice-dialog";
import { useInvoices } from "./use-billing";
import { TableScroll } from "./table-scroll";

const COLUMNS = 6;
const TONE_STATUS: Record<string, StatusKind> = { success: "success", warning: "warning", danger: "danger", neutral: "neutral", brand: "info" };

function useInvoiceActions() {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const handleOrder = useHandleOrder();
  const pay = useMutation({
    mutationFn: ({ id, body }: { id: string; body?: CheckoutChoiceBody }) =>
      unwrap(api.POST("/v1/billing/invoices/{invoice_id}/pay", { params: { path: { invoice_id: id } }, body: body ?? null })),
    onSuccess: (order) => { handleOrder(order, t("billing.invoices.paid")); },
    onError: (caught) => toast.error(message(caught)),
  });
  const pdf = useMutation({
    mutationFn: (id: string) => unwrap(api.GET("/v1/billing/invoices/{invoice_id}/pdf", { params: { path: { invoice_id: id } } })),
    onSuccess: (download) => { startDownload(download.url); },
    onError: (caught) => toast.error(message(caught)),
  });
  return { pay, pdf };
}

interface InvoiceRowProps {
  invoice: InvoiceOut;
  canManage: boolean;
  actions: ReturnType<typeof useInvoiceActions>;
  onPay: (invoice: InvoiceOut) => void;
}

function InvoiceRow({ invoice, canManage, actions, onPay }: InvoiceRowProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const status = invoiceStatus(invoice, new Date());
  const label = t(`billing.invoices.status_${status.key}`, { date: fmt.shortDate(invoice.due_date) });
  return (
    <tr>
      <td className="whitespace-nowrap font-mono text-xs font-semibold">{invoice.number}</td>
      <td className="whitespace-nowrap text-fg-muted">{fmt.date(invoice.issued_at ?? invoice.created_at)}</td>
      <td>
        {invoice.description ?? "—"}
        {invoice.or_number ? <div className="text-xs text-fg-subtle">{t("billing.invoices.or", { number: invoice.or_number })}</div> : null}
      </td>
      <td className="text-right tabular-nums">
        {fmt.moneyExact(invoice.total, invoice.currency)}
        {Number(invoice.tax) > 0 ? (
          <div className="text-[11px] text-fg-subtle">{t("billing.invoices.inclTax", { tax: fmt.moneyExact(invoice.tax, invoice.currency), label: invoice.tax_label ?? "" })}</div>
        ) : invoice.tax_note ? (
          <div className="text-[11px] text-fg-subtle">{invoice.tax_label ?? invoice.tax_note}</div>
        ) : null}
      </td>
      <td>
        <StatusBadge status={TONE_STATUS[status.tone] ?? "neutral"} icon={status.icon} label={label} />
      </td>
      <td className="whitespace-nowrap text-right">
        {status.payable ? (
          <GatedButton allowed={canManage} size="sm" loading={actions.pay.isPending && actions.pay.variables.id === invoice.id} onClick={() => { onPay(invoice); }}>
            {t("billing.invoices.payNow")}
            <span className="sr-only"> {invoice.number}</span>
          </GatedButton>
        ) : (
          <Button variant="ghost" size="sm" loading={actions.pdf.isPending && actions.pdf.variables === invoice.id} onClick={() => { actions.pdf.mutate(invoice.id); }}>
            <Icon name="download" />
            {t("billing.invoices.pdf")}
            <span className="sr-only"> {invoice.number}</span>
          </Button>
        )}
      </td>
    </tr>
  );
}

/** Invoices & official receipts: server-side search, pay open invoices, PDF downloads. */
export function InvoicesCard({ canManage, gateways }: { canManage: boolean; gateways: readonly string[] }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [paying, setPaying] = useState<InvoiceOut | null>(null);
  const invoices = useInvoices(search);
  const actions = useInvoiceActions();
  const startPay = (invoice: InvoiceOut) => {
    if (needsGatewayChoice(gateways)) setPaying(invoice);
    else actions.pay.mutate({ id: invoice.id });
  };
  const rows = invoices.data ?? [];
  return (
    <section className="card overflow-hidden" aria-labelledby="invoices-title">
      <div className="card-header">
        <h2 id="invoices-title" className="card-title">
          {t("billing.invoices.title")}
        </h2>
        <Input className="h-8 w-48 text-xs" type="search" placeholder={t("billing.invoices.search")} aria-label={t("billing.invoices.search")} value={search} onChange={(event) => { setSearch(event.target.value); }} />
      </div>
      {invoices.isError ? (
        <ErrorState headingLevel={3} title={t("billing.invoices.loadError")} onRetry={() => void invoices.refetch()} retryLabel={t("common.retry")} />
      ) : (
        <TableScroll label={t("billing.invoices.title")}>
          <table className="table">
            <thead>
              <tr>
                <th>{t("billing.invoices.number")}</th>
                <th>{t("billing.invoices.date")}</th>
                <th>{t("billing.invoices.description")}</th>
                <th className="text-right">{t("billing.invoices.amount")}</th>
                <th>{t("billing.invoices.status")}</th>
                <th className="text-right">
                  <span className="sr-only">{t("billing.invoices.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.isPending ? (
                <TableSkeletonRows rows={4} columns={COLUMNS} />
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS}>
                    <EmptyState
                      icon="file-text"
                      headingLevel={3}
                      title={search ? t("billing.invoices.noMatchTitle") : t("billing.invoices.emptyTitle")}
                      description={search ? t("billing.invoices.noMatchBody") : t("billing.invoices.emptyBody")}
                      action={search ? <Button variant="secondary" onClick={() => { setSearch(""); }}>{t("billing.invoices.clear")}</Button> : undefined}
                    />
                  </td>
                </tr>
              ) : (
                rows.map((invoice) => <InvoiceRow key={invoice.id} invoice={invoice} canManage={canManage} actions={actions} onPay={startPay} />)
              )}
            </tbody>
          </table>
        </TableScroll>
      )}
      {paying ? (
        <PayInvoiceDialog
          invoice={paying}
          gateways={gateways}
          paying={actions.pay.isPending}
          onPay={(body) => { actions.pay.mutate({ id: paying.id, body }, { onSettled: () => { setPaying(null); } }); }}
          onClose={() => { setPaying(null); }}
        />
      ) : null}
    </section>
  );
}
